import { cn } from "@/lib/utils";
import { ChatsCircleIcon, NotePencilIcon } from "@phosphor-icons/react";
import { isThisYear, isToday, isYesterday } from "date-fns";
import type { DateFormat } from "@/features/utils/dates";
import { useDateFormat, usePublicTranslation } from "@/features/utils/i18n";
import { type PublicConversation } from "../../hooks/use-public-conversations";
import { CloseAction } from "./ChatHeader";

/**
 * The list is the root, so it has no "back": there is nothing behind it. Returning to a
 * conversation is picking it out of the list — including the one in progress, which is
 * the first row and says so. An arrow here pointed at nothing.
 *
 * Nor a pencil: "Poser une nouvelle question" sits at the bottom of the list, under the
 * thumb, saying it in words. Two ways to do one thing, one of them mute, is how a
 * visitor wonders whether they differ.
 */
export function ConversationsHeader() {
  const { t } = usePublicTranslation();
  return (
    <div className="flex items-center gap-1 border-b bg-background/80 px-2 py-2 backdrop-blur">
      <h1 className="min-w-0 flex-1 truncate px-2 text-base font-semibold">
        {t("chat.conversations")}
      </h1>
      <CloseAction />
    </div>
  );
}

// A conversation list is read by "when", so the date is the label: the time today,
// the weekday this week, the date beyond.
function conversationDate(
  timestamp: number,
  { format }: DateFormat,
  yesterday: string,
): string {
  if (!timestamp) return "";
  const date = new Date(timestamp);
  if (isToday(date)) return format(date, "p");
  if (isYesterday(date)) return yesterday;
  if (isThisYear(date)) return format(date, "d MMM");
  return format(date, "d MMM yyyy");
}

interface ChatConversationsProps {
  conversations: PublicConversation[];
  isLoading: boolean;
  onSelect: (publicId: string) => void;
  onNewConversation: () => void;
  isStartingConversation?: boolean;
}

/**
 * The visitor's threads, newest first. Replaces the message list (and the composer:
 * there is nothing to type here) while it is on screen.
 */
export function ChatConversations({
  conversations,
  isLoading,
  onSelect,
  onNewConversation,
  isStartingConversation,
}: ChatConversationsProps) {
  const { t } = usePublicTranslation();
  const dates = useDateFormat();
  return (
    <div className="flex min-h-0 flex-1 flex-col">
      {/* A plain scroller, not the shared ScrollArea: Radix wraps its viewport in a
          `display: table` box that sizes to its content, so a row's `truncate` never
          bites and a long preview pushes the date off the panel. */}
      <div className="min-h-0 flex-1 overflow-y-auto overscroll-contain px-3">
        <div className="space-y-1 py-3">
          {isLoading && (
            <p className="px-2 py-6 text-center text-sm text-muted-foreground">
              {t("chat.loading")}
            </p>
          )}

          {!isLoading && conversations.length === 0 && (
            <div className="flex flex-col items-center gap-3 px-6 py-10 text-center text-muted-foreground">
              <ChatsCircleIcon className="size-8" />
              <p className="text-sm">{t("chat.conversations_empty")}</p>
            </div>
          )}

          {conversations.map((conversation) => (
            <button
              key={conversation.public_id}
              type="button"
              onClick={() => onSelect(conversation.public_id)}
              className={cn(
                "flex w-full items-start gap-3 rounded-[var(--chat-radius,1rem)] px-3 py-3 text-left transition-colors hover:bg-muted/60",
                conversation.is_current && "bg-muted/50",
              )}
            >
              <div className="min-w-0 flex-1">
                <div className="flex items-baseline gap-2">
                  <span className="min-w-0 flex-1 truncate text-sm font-medium">
                    {conversation.name || t("chat.conversation_untitled")}
                  </span>
                  <span className="shrink-0 text-[11px] text-muted-foreground">
                    {conversationDate(
                      conversation.updated_at,
                      dates,
                      t("chat.yesterday"),
                    )}
                  </span>
                </div>
                <p className="truncate text-xs text-muted-foreground">
                  {conversation.preview || t("chat.conversation_no_message")}
                </p>
                {conversation.is_current && (
                  <p className="mt-1 flex items-center gap-1.5 text-[11px] font-medium text-success-foreground">
                    <span className="inline-block size-1.5 rounded-full bg-success" />
                    {t("chat.conversation_current")}
                  </p>
                )}
              </div>
            </button>
          ))}
        </div>
      </div>

      {/* The way out of the list is also the point of it: asking something new. */}
      <div className="border-t bg-background/90 px-3 py-3 backdrop-blur">
        <button
          type="button"
          onClick={onNewConversation}
          disabled={isStartingConversation}
          className="flex w-full items-center justify-center gap-2 rounded-[var(--chat-radius,1rem)] bg-primary px-4 py-3 text-sm font-semibold text-primary-foreground transition-colors hover:bg-primary/90 active:scale-[0.99] disabled:opacity-60"
        >
          <NotePencilIcon className="size-4" weight="bold" />
          {isStartingConversation ? t("chat.opening") : t("chat.new_question")}
        </button>
      </div>
    </div>
  );
}
